import type { ApolloLink } from '@apollo/client';
import { ApolloClient, ApolloProvider, InMemoryCache, split } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuth0 } from '@auth0/auth0-react';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { createClient } from 'graphql-ws';
import React, { useMemo, useRef } from 'react';

import { config } from '../config/loader.js';

interface Props {
  children: React.ReactNode;
}

function getWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws').replace(/\/$/, '') + '/graphql';
}

export default function GraphQLProvider({ children }: Props): React.JSX.Element {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const tokenRef = useRef<string | null>(null);

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }: { headers?: Record<string, string> }) => {
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        tokenRef.current = token;
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : '',
          },
        };
      }
      return { headers };
    });

    const uploadLink = createUploadLink({
      uri: config.backendUrl + '/graphql',
      headers: { 'apollo-require-preflight': 'true' },
    }) as unknown as ApolloLink;

    const wsLink = new GraphQLWsLink(
      createClient({
        url: getWsUrl(config.backendUrl),
        connectionParams: async () => {
          if (isAuthenticated) {
            const token = await getAccessTokenSilently();
            tokenRef.current = token;
            return {
              authorization: token ? `Bearer ${token}` : '',
            };
          }
          return {};
        },
        retryAttempts: 5,
        shouldRetry: () => true,
      }),
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      authLink.concat(uploadLink),
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
    });
  }, [isAuthenticated, getAccessTokenSilently]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
